/**
 * Consonant sound values, looked up by vocabulary class id.
 *
 * The same join as `glyphs.ts` and against the same key: the generated table is ordered by the
 * append-only vocab class id, and `TokenOut.letter_class` / `VocabEntryOut.id` are that id. So a
 * sound value beside a letter is a join, not a new data source, and nothing here fetches.
 *
 * Two refusals are structural rather than a caller's responsibility:
 *
 * * **A class with no value never gets one invented.** `illegible` and `unknown` are `ipa: null`
 *   and always will be — a value on a special is a hard failure in `phonology.py` — so callers get
 *   `null` and must render a gap, exactly as `GlyphImage.svelte` does for a missing form.
 * * **A contested value is never returned as though it were settled.** `certainty` rides on every
 *   row and `isContested` exists so a caller cannot read the IPA without the grade being available
 *   in the same object.
 *
 * These are *consonants*. The script records no vowels, so nothing here is one and nothing here
 * can be assembled into a spoken word on its own — that needs a separate, weaker, separately
 * graded claim. Do not add vowels to this module.
 */

import {
  PHONOLOGY,
  PHONOLOGY_SOURCE,
  REFERENCES,
  type Reference,
  type SoundValue,
} from './phonology.generated';

const BY_CLASS_ID: ReadonlyMap<number, SoundValue> = new Map(
  PHONOLOGY.map((value) => [value.letterClass, value]),
);

const BY_KEY: ReadonlyMap<string, Reference> = new Map(
  REFERENCES.map((reference) => [reference.key, reference]),
);

/** The table row for a vocabulary class, or `undefined` if the id is not one. */
export function soundValue(letterClass: number): SoundValue | undefined {
  return BY_CLASS_ID.get(letterClass);
}

/**
 * Whether this class has a recorded consonant value at all.
 *
 * False for `illegible`, `unknown`, and any id that is not a class — a caller can do nothing
 * different about those three, so they collapse to one answer.
 */
export function hasSoundValue(letterClass: number): boolean {
  return Boolean(BY_CLASS_ID.get(letterClass)?.ipa);
}

/** True when rival readings exist for this class and are listed on the row. */
export function isContested(letterClass: number): boolean {
  return BY_CLASS_ID.get(letterClass)?.certainty === 'contested';
}

/** The works cited by a row, resolved against the bibliography. Unknown keys cannot occur. */
export function citationsFor(letterClass: number): Reference[] {
  const value = BY_CLASS_ID.get(letterClass);
  if (!value) return [];
  return value.sources
    .map((citation) => BY_KEY.get(citation.key))
    .filter((reference): reference is Reference => Boolean(reference));
}

export { PHONOLOGY_SOURCE, REFERENCES };
export type { Reference, SoundValue };
